import React, { useState, useEffect } from 'react';
import { useLocation, useRoute } from 'wouter';
import { useUser } from '@/contexts/UserContext';
import { api } from '@/config/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  ArrowLeft,
  Eye,
  Search,
  Calendar,
  Camera,
  User,
  FileImage,
  Download,
  Filter,
  Info
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface Photo {
  id: string;
  filename: string;
  storageurl: string;
  thumbnailurl: string;
  resolution: string;
  processingstatus: string;
  visibility: string;
  author?: string;
  capture_datetime?: string;
  camera_model?: string;
  original_filename?: string;
  bibnumbers?: string;
  metadata: {
    cameraModel?: string;
    author?: string;
    originalName?: string;
    fileSize?: number;
    captureDateTime?: string;
    technical?: {
      file_size_bytes?: number;
      format?: string;
      mode?: string;
      width?: number;
      height?: number;
      orientation?: number;
      color?: string;
      bits_per_channel?: number;
      dpi?: number;
      capture_datetime?: string;
      gps_latitude?: number;
      gps_longitude?: number;
      camera?: string;
      camera_make?: string;
      author?: string;
    };
  };
  uploadedat: string;
  createdat: string;
}

interface BatchPhotosResponse {
  photos: Photo[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const BatchPhotosPage: React.FC = () => {
  const [, setLocation] = useLocation();
  const [, params] = useRoute('/uploader/photos/:batchId');
  const { user, isLoading } = useUser();

  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPhotos, setTotalPhotos] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [batchInfo, setBatchInfo] = useState<any>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [showMetadataModal, setShowMetadataModal] = useState(false);

  const batchId = params?.batchId;
  const limit = 10;

  // Buscar fotos do batch
  const fetchBatchPhotos = async (page: number = 1) => {
    if (!batchId || !user?.id) return;

    try {
      setLoading(true);
      const response = await api.get(`/api/photo-batches/${batchId}/photos?page=${page}&limit=${limit}&search=${searchTerm}`);

      if (response.data.success) {
        const data: BatchPhotosResponse = response.data.data;
        setPhotos(data.photos);
        setTotalPages(data.totalPages);
        setTotalPhotos(data.total);
        setCurrentPage(data.page);
      }
    } catch (error) {
      console.error('Erro ao buscar fotos do lote:', error);
      toast({
        title: "Erro ao carregar fotos",
        description: "Não foi possível carregar as fotos do lote.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Buscar informações do batch
  const fetchBatchInfo = async () => {
    if (!batchId) return;

    try {
      const response = await api.get(`/api/photo-batches/${batchId}`);
      if (response.data.success) {
        setBatchInfo(response.data.data);
      }
    } catch (error) {
      console.error('Erro ao buscar informações do lote:', error);
    }
  };

  // Buscar dados quando o componente montar
  useEffect(() => {
    if (user?.id && batchId) {
      fetchBatchInfo();
      fetchBatchPhotos();
    }
  }, [user?.id, batchId]);

  // Buscar fotos quando a página ou termo de busca mudar
  useEffect(() => {
    if (user?.id && batchId) {
      fetchBatchPhotos(1);
    }
  }, [searchTerm]);

  // Redirecionar se não estiver logado
  useEffect(() => {
    if (!isLoading && !user) {
      setLocation('/login');
    }
  }, [isLoading, user, setLocation]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchBatchPhotos(page);
  };

  const handlePhotoPreview = (photo: Photo) => {
    setLocation(`/photo/${photo.id}`);
  };

  const handleShowMetadata = (photo: Photo) => {
    setSelectedPhoto(photo);
    setShowMetadataModal(true);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground text-lg font-medium">Carregando página...</p>
          <p className="text-muted-foreground text-sm mt-2">Verificando informações do lote</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLocation('/uploader/dashboard')}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Voltar ao Dashboard</span>
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Fotos do Lote</h1>
              {batchInfo && (
                <p className="text-muted-foreground">
                  {batchInfo.batchName} • {totalPhotos} fotos
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Filtros e Busca */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome do arquivo, autor ou modelo da câmera..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button variant="outline" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                Filtros
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabela de Fotos */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <FileImage className="h-5 w-5" />
              <span>Lista de Fotos</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
                <p className="text-muted-foreground text-sm">Carregando fotos...</p>
                <p className="text-muted-foreground text-xs mt-1">Aguarde enquanto buscamos as informações</p>
              </div>
            ) : !photos || photos.length === 0 ? (
              <div className="text-center py-8">
                <FileImage className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Nenhuma foto encontrada neste lote.</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">ID</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">Arquivo</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">Câmera</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">Autor</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">Bib Numbers</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">Data Captura</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">Tamanho</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">Resolução</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">Data Upload</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(photos || []).map((photo, index) => (
                        <tr key={photo.id} className={`border-b border-border hover:bg-muted/50 transition-colors ${index % 2 === 0 ? 'bg-background' : 'bg-muted/20'}`}>
                          <td className="py-4 px-4">
                            <div className="font-mono text-sm font-medium text-primary">
                              #{String((currentPage - 1) * limit + index + 1).padStart(3, '0')}
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-sm font-medium">{photo.original_filename || photo.filename}</span>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center space-x-2">
                              <Camera className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">{photo.camera_model || '-'}</span>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center space-x-2">
                              <User className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">{photo.author || '-'}</span>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex flex-wrap gap-1">
                              {photo.bibnumbers ? (
                                (() => {
                                  try {
                                    const bibNumbers = JSON.parse(photo.bibnumbers);
                                    if (Array.isArray(bibNumbers) && bibNumbers.length > 0) {
                                      return bibNumbers.map((bib: string, idx: number) => (
                                        <Badge key={idx} variant="secondary" className="text-xs">
                                          {bib}
                                        </Badge>
                                      ));
                                    }
                                    return <span className="text-xs text-muted-foreground">-</span>;
                                  } catch {
                                    return <span className="text-xs text-muted-foreground">-</span>;
                                  }
                                })()
                              ) : (
                                <span className="text-xs text-muted-foreground">-</span>
                              )}
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center space-x-2">
                              <Calendar className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">
                                {photo.capture_datetime ? formatDate(photo.capture_datetime) : '-'}
                              </span>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-sm">{photo.metadata?.technical?.file_size_bytes ? formatFileSize(photo.metadata.technical.file_size_bytes) : '-'}</span>
                          </td>
                          <td className="py-4 px-4">
                            <Badge variant="outline" className="text-xs">
                              {photo.resolution}
                            </Badge>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center space-x-2">
                              <Calendar className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">{formatDate(photo.uploadedat)}</span>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center space-x-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handlePhotoPreview(photo)}
                                className="h-8 w-8 p-0 hover:bg-blue-100 dark:hover:bg-blue-900"
                                title="Visualizar foto"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleShowMetadata(photo)}
                                className="h-8 w-8 p-0 hover:bg-green-100 dark:hover:bg-green-900"
                                title="Visualizar metadados"
                              >
                                <Info className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Paginação */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-6">
                    <div className="text-sm text-muted-foreground">
                      Mostrando {((currentPage - 1) * limit) + 1} a {Math.min(currentPage * limit, totalPhotos)} de {totalPhotos} fotos
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Anterior
                      </Button>

                      {/* Números das páginas */}
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                          if (pageNum > totalPages) return null;

                          return (
                            <Button
                              key={pageNum}
                              variant={pageNum === currentPage ? "default" : "outline"}
                              size="sm"
                              onClick={() => handlePageChange(pageNum)}
                              className="w-8 h-8 p-0"
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Próximo
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modal de Metadados */}
      {showMetadataModal && selectedPhoto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Metadados da Foto</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowMetadataModal(false)}
                className="h-8 w-8 p-0"
              >
                ×
              </Button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Arquivo</label>
                  <p className="text-sm">{selectedPhoto.original_filename || selectedPhoto.filename}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Autor</label>
                  <p className="text-sm">{selectedPhoto.author || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Câmera</label>
                  <p className="text-sm">{selectedPhoto.camera_model || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Resolução</label>
                  <p className="text-sm">{selectedPhoto.resolution}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Data de Captura</label>
                  <p className="text-sm">{selectedPhoto.capture_datetime ? formatDate(selectedPhoto.capture_datetime) : '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Data de Upload</label>
                  <p className="text-sm">{formatDate(selectedPhoto.uploadedat)}</p>
                </div>
              </div>
              {selectedPhoto.metadata?.technical && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Metadados Técnicos</label>
                  <div className="mt-2 p-4 bg-muted rounded-lg">
                    <pre className="text-xs overflow-x-auto">
                      {JSON.stringify(selectedPhoto.metadata.technical, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
              {selectedPhoto.bibnumbers && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Bib Numbers</label>
                  <p className="text-sm">{selectedPhoto.bibnumbers}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default BatchPhotosPage;
